/**
 * Hook to get real-time sync status from repository events
 */

console.log('[use-sync-status.ts] Module loaded');

import { useEffect, useState, useRef } from 'react';
import { runEffect, runEffectFork } from './use-repository-runtime';
import type { SyncMetadata } from '@/services/sync/sync-state';
import type { RepositoryEvent } from '@/services/events/repository-events';
import * as Effect from 'effect/Effect';
import * as Stream from 'effect/Stream';
import { SyncService } from '@/services/sync/sync.service';
import { StorageService } from '@/services/storage/storage.service';
import { RepositoryEvents } from '@/services/events/repository-events';

export interface SyncStatus {
  pending: number;
  failed: number;
  synced: number;
  isSyncing: boolean;
  lastSyncAt: string | null;
  error: string | null;
}

/**
 * Get sync status for a specific group
 * Combines metadata from storage with real-time events
 */
export function useSyncStatus(groupId: string): SyncStatus {
  const [metadata, setMetadata] = useState<SyncMetadata | null>(null);
  const [unsyncedCount, setUnsyncedCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Debounce timer ref to batch transaction event updates
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch initial sync metadata and unsynced count
  useEffect(() => {
    console.log('[useSyncStatus] useEffect running for groupId:', groupId);
    if (!groupId) {
      console.log('[useSyncStatus] No groupId, returning early');
      return;
    }

    const effect = Effect.gen(function* () {
      const syncService = yield* SyncService;
      const storage = yield* StorageService;

      const meta = yield* syncService.getSyncStatus(groupId);
      const transactions = yield* storage.getTransactionsByGroup(groupId);
      const unsynced = transactions.filter(t => !t.is_synced && !t.deleted);

      return { meta, unsyncedCount: unsynced.length };
    });

    runEffect(effect)
      .then(result => {
        console.log('[useSyncStatus] Initial load for group', groupId, '- unsynced count:', result.unsyncedCount);
        setMetadata(result.meta);
        setUnsyncedCount(result.unsyncedCount);
      })
      .catch((err: unknown) => {
        console.error('Failed to get sync status:', err);
        const message = err instanceof Error ? err.message : 'Failed to get sync status';
        setError(message);
      });
  }, [groupId]);

  // Subscribe to events for real-time updates
  useEffect(() => {
    if (!groupId) return;

    const effect = Effect.gen(function* () {
      const repositoryEvents = yield* RepositoryEvents;
      const stream = repositoryEvents.subscribe();

      // Filter for sync AND transaction events for this group
      const relevantEvents = Stream.filter(stream, (event: RepositoryEvent) => {
        if (!('groupId' in event) || event.groupId !== groupId) return false;
        const isRelevant = event._tag === 'SyncStateChanged' ||
               event._tag === 'SyncProgress' ||
               event._tag === 'SyncComplete' ||
               event._tag === 'SyncFailed' ||
               event._tag === 'TransactionCreated' ||
               event._tag === 'TransactionUpdated' ||
               event._tag === 'TransactionDeleted';
        if (isRelevant) {
          console.log('[useSyncStatus] Received event:', event._tag, 'for group', groupId);
        }
        return isRelevant;
      });

      // Update metadata and counts based on events
      // Debounce transaction events to avoid excessive re-renders
      yield* Stream.runForEach(relevantEvents, (event) =>
        Effect.sync(() => {
          // Clear existing timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          // Debounce transaction events, but update sync events immediately
          const isTransactionEvent = event._tag === 'TransactionCreated' ||
                                     event._tag === 'TransactionUpdated' ||
                                     event._tag === 'TransactionDeleted';

          const updateStatus = () => {
            const effect = Effect.gen(function* () {
              const syncService = yield* SyncService;
              const storage = yield* StorageService;

              const meta = yield* syncService.getSyncStatus(groupId);
              const transactions = yield* storage.getTransactionsByGroup(groupId);
              const unsynced = transactions.filter(t => !t.is_synced && !t.deleted);

              yield* Effect.sync(() => {
                console.log('[useSyncStatus] Event update - unsynced count:', unsynced.length, 'total:', transactions.length);
                setMetadata(meta);
                setUnsyncedCount(unsynced.length);
              });
            });

            runEffect(effect).catch(console.error);
          };

          if (isTransactionEvent) {
            // Debounce transaction events by 300ms
            debounceTimerRef.current = setTimeout(updateStatus, 300);
          } else {
            // Update immediately for sync events
            updateStatus();
          }
        })
      );
    });

    // Subscribe using shared runtime
    let cleanup: (() => void) | null = null;
    runEffectFork(effect).then(c => { cleanup = c; });

    return () => {
      // Clear debounce timer on cleanup
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (cleanup) cleanup();
    };
  }, [groupId]);

  const isSyncing = metadata?.state._tag === 'Fetching' || metadata?.state._tag === 'Applying';
  const lastSyncAt = metadata?.lastFullSyncAt || metadata?.lastIncrementalSyncAt || null;

  // Extract error message from state
  const stateError = metadata?.state._tag === 'SyncError' && 'error' in metadata.state
    ? (typeof metadata.state.error === 'string' ? metadata.state.error : metadata.state.error?.message || null)
    : null;

  return {
    pending: unsyncedCount,
    failed: metadata?.state._tag === 'SyncError' ? 1 : 0,
    synced: metadata?.state._tag === 'Complete' && 'recordsSynced' in metadata.state ? metadata.state.recordsSynced : 0,
    isSyncing,
    lastSyncAt,
    error: stateError || error,
  };
}

/**
 * Get aggregate sync status across all groups
 * Useful for showing global sync indicators
 */
export function useGlobalSyncStatus(): SyncStatus {
  const [allMetadata, setAllMetadata] = useState<SyncMetadata[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch sync metadata for all groups
  useEffect(() => {
    const effect = Effect.gen(function* () {
      const storage = yield* StorageService;
      const syncService = yield* SyncService;

      // Get all groups
      const groups = yield* storage.getAllGroups();

      // Get sync metadata for each group
      const metadataArray: SyncMetadata[] = [];

      for (const group of groups) {
        const meta = yield* Effect.orElseSucceed(
          syncService.getSyncStatus(group.id),
          () => null
        );
        if (meta) metadataArray.push(meta);
      }

      return metadataArray;
    });

    runEffect(effect)
      .then(setAllMetadata)
      .catch((err: unknown) => {
        console.error('Failed to get global sync status:', err);
        const message = err instanceof Error ? err.message : 'Failed to get global sync status';
        setError(message);
      });
  }, []);

  // Subscribe to sync events for all groups
  useEffect(() => {
    const effect = Effect.gen(function* () {
      const repositoryEvents = yield* RepositoryEvents;
      const stream = repositoryEvents.subscribe();

      // Filter for sync-related events
      const syncEvents = Stream.filter(stream, (event: RepositoryEvent) => {
        return event._tag === 'SyncStateChanged' ||
               event._tag === 'SyncProgress' ||
               event._tag === 'SyncComplete' ||
               event._tag === 'SyncFailed';
      });

      // Refetch all metadata when any sync event occurs
      yield* Stream.runForEach(syncEvents, () =>
        Effect.gen(function* () {
          const storage = yield* StorageService;
          const syncService = yield* SyncService;

          const groups = yield* storage.getAllGroups();

          const metadataArray: SyncMetadata[] = [];

          for (const group of groups) {
            const meta = yield* Effect.orElseSucceed(
              syncService.getSyncStatus(group.id),
              () => null
            );
            if (meta) metadataArray.push(meta);
          }

          yield* Effect.sync(() => setAllMetadata(metadataArray));
        })
      );
    });

    // Subscribe using shared runtime
    let cleanup: (() => void) | null = null;
    runEffectFork(effect).then(c => { cleanup = c; });

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Aggregate status from all groups
  const isSyncing = allMetadata.some(m => m.state._tag === 'Fetching' || m.state._tag === 'Applying');
  const failed = allMetadata.filter(m => m.state._tag === 'SyncError').length;
  const synced = allMetadata
    .filter(m => m.state._tag === 'Complete')
    .reduce((sum, m) => sum + ('recordsSynced' in m.state ? m.state.recordsSynced : 0), 0);

  // Get most recent sync timestamp
  const lastSyncAt = allMetadata
    .map(m => m.lastFullSyncAt || m.lastIncrementalSyncAt)
    .filter((ts): ts is string => ts !== null)
    .sort()
    .reverse()[0] || null;

  // Get first error if any
  const firstError = allMetadata
    .filter(m => m.state._tag === 'SyncError')
    .map(m => 'error' in m.state ? (typeof m.state.error === 'string' ? m.state.error : m.state.error?.message) : null)
    .filter((err): err is string => err !== null)[0] || null;

  return {
    pending: 0, // TODO: Calculate from unsynced transactions across all groups
    failed,
    synced,
    isSyncing,
    lastSyncAt,
    error: firstError || error,
  };
}
